'use client';

import Link from 'next/link';
import NextImage from 'next/image';
import React, { useState } from 'react';
import { useTranslation } from '@/app/i18n/TranslationContext';
import {
  useIsDesktop,
  handleArrowKeyFocus,
  handleRedirectClick,
} from '@/helpers/utils';
import FacebookIcon from '@/public/images/footer/facebook-rounded.svg';
import InstagramIcon from '@/public/images/footer/instagram-rounded.svg';
import styles from './Footer.module.scss';
import WonderfulPistachiosLogo from '@/components/static/WonderfulPistachiosLogo';
import Popup from '@/components/modules/Popup/Popup';
import { PopupProps } from '@/components/modules/Popup/Popup.types';
import { removeDefaultLocaleFromPath } from '@/app/i18n/utils';

export default function Footer({ locale }: { locale: string }) {
  const isDesktop = useIsDesktop();
  const { t } = useTranslation();

  const copyrightText = (
    <p key="copyright" className={styles.copyrightLink}>
      {`© ${new Date().getFullYear()} ${t(
        'Wonderful Pistachios & Almonds LLC.'
      )}`}
    </p>
  );

  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [redirectLink, setRedirectLink] = useState('');

  const popupProps = {
    label: t('We’ll miss you!'),
    description: t(
      `You are now leaving the www.wonderfulpistachios.com site for Europe, Middle East, and Africa.`
    ),
    isPopupOpen: isPopupOpen,
    setIsPopupOpen: setIsPopupOpen,
    redirectLink: redirectLink,
    setRedirectLink: setRedirectLink,
    cancelBtnText: t('Go back'),
    acceptBtnText: t('Continue'),
  };

  return (
    <>
      {isPopupOpen && <Popup {...popupProps} />}
      <div className={styles.getCrackinContainer}>
        <div className={styles.openingShellImageContainer}>
          <NextImage
            src="/images/footer/emerging-from-shell.png"
            fill
            style={{ objectFit: 'contain' }}
            alt=""
          />
        </div>
        <strong className={styles.getCrackinText}>Get Crackin’&lrm;</strong>
      </div>
      <footer className={styles.footer}>
        <div className="container">
          <div className={styles.mainContent}>
            <Link
              href={removeDefaultLocaleFromPath(`/${locale}`)}
              className={styles.ownLogo}
              aria-label="Wonderful Pistachios Logo"
            >
              <WonderfulPistachiosLogo />
            </Link>
            <SiteAndSocialLinks locale={locale} key="site-links" />
          </div>
          <div className={styles.brands}>
            <nav aria-label="Wonderful Company brands">
              <ul className={styles.brandsRow} data-testid={'footer-brands'}>
                <BrandLink
                  href="https://www.wonderful.com/"
                  img="/images/footer/logo_twc.svg"
                  alt="The Wonderful Company"
                  height={27}
                  width={271}
                  popupProps={popupProps}
                />
                <BrandLink
                  href="https://www.wonderfulpistachios.com/"
                  img="/images/footer/logo_pistachios-grey.svg"
                  alt="Wonderful Pistachios"
                  height={32}
                  width={112}
                  popupProps={popupProps}
                />
                <BrandLink
                  href="https://www.pomwonderful.com/"
                  img="/images/footer/logo_pom.svg"
                  alt="Pom Wonderful"
                  height={34}
                  width={73}
                  popupProps={popupProps}
                />
              </ul>
            </nav>
          </div>
          <div className={styles.bottomItems} data-testid={'footer-bottom'}>
            {isDesktop
              ? [
                  copyrightText,
                  <LegalLinks key="legal-links" locale={locale} />,
                ]
              : [
                  <LegalLinks key="legal-links" locale={locale} />,
                  copyrightText,
                ]}
          </div>
        </div>
      </footer>
    </>
  );
}

const getFirstProductsLinks = (t: any) => [
  { text: t('all'), link: '/products' },
  { text: t('in-shell'), link: `/products?filter=${t('in-shell')}` },
  { text: t('no shells'), link: `/products?filter=${t('no shells')}` },
];

const getSecondProductsLinks = (t: any) => [
  { text: t('almonds'), link: `/products?filter=${t('almonds')}` },
  { text: t('flavours'), link: `/products?filter=${t('flavours')}` },
  { text: t('where to buy'), link: '/where-to-buy' },
];

const getAboutUsLinks = (t: any) => [
  { text: t('contact us'), link: '/contact' },
  { text: t('FAQs'), link: '/faq' },
  { text: t('sitemap'), link: '/sitemap' },
];

const SiteAndSocialLinks = React.memo(({ locale }: { locale: string }) => {
  const { t } = useTranslation();
  const renderSiteLink = (
    link: { text: string; link: string },
    index: number
  ) => (
    <li key={index}>
      <Link href={removeDefaultLocaleFromPath(`/${locale}${link.link}`)}>
        {link.text}
      </Link>
    </li>
  );

  const handleHorizontalArrows = handleArrowKeyFocus(
    'horizontal',
    `.${styles.siteLinksColumn}`
  );
  const handleVerticalArrows = handleArrowKeyFocus('vertical');

  return (
    <nav
      className={styles.siteLinks}
      onKeyDown={e => {
        handleHorizontalArrows(e);
        handleVerticalArrows(e);
      }}
      aria-label="footer links"
      data-testid={'footer-links'}
    >
      <div className={styles.siteLinksColumn} data-testid={'product-links'}>
        <strong className={styles.siteLinksHeader}>{t('products')}</strong>
        <div className={styles.siteLinksColumnWrap}>
          <ul>{getFirstProductsLinks(t).map(renderSiteLink)}</ul>
          <ul>{getSecondProductsLinks(t).map(renderSiteLink)}</ul>
        </div>
      </div>
      <div className={styles.siteLinksColumn} data-testid={'about-links'}>
        <strong className={styles.siteLinksHeader}>{t('about us')}</strong>
        <ul>{getAboutUsLinks(t).map(renderSiteLink)}</ul>
      </div>
      {locale !== 'de' && locale !== 'nl' && (
        <div className={styles.siteLinksColumn} data-testid={'footer-social'}>
          <strong className={styles.siteLinksHeader}>{t('follow us')}</strong>
          <SocialLinks locale={locale} />
        </div>
      )}
    </nav>
  );
});
// fixes react.memo display name eslint issue
SiteAndSocialLinks.displayName = 'SiteAndSocialLinks';

interface BrandLinkProps {
  href: string;
  img: string;
  alt: string;
  height: number;
  width: number;
  popupProps?: PopupProps | undefined;
}

const BrandLink = React.memo((props: BrandLinkProps) => {
  const { popupProps } = props;

  return (
    <li className={styles.brandLink}>
      <a
        href={props.href}
        target={popupProps ? '_blank' : '_self'}
        rel="noopener noreferrer"
        onClick={
          popupProps
            ? event =>
                handleRedirectClick(
                  props?.href,
                  popupProps?.isPopupOpen,
                  popupProps?.setIsPopupOpen,
                  popupProps?.setRedirectLink,
                  event
                )
            : undefined
        }
      >
        <NextImage
          loading="lazy"
          src={props.img}
          alt={`${props.alt} (opens in new tab)`}
          height={props.height}
          width={props.width}
        />
      </a>
    </li>
  );
});

BrandLink.displayName = 'BrandLink';

const LegalLinks = React.memo(({ locale }: { locale: string }) => {
  const { t } = useTranslation();
  return (
    <ul className={styles.legalLinks} aria-label="legal">
      <li className={styles.legalLink}>
        <Link href={removeDefaultLocaleFromPath(`/${locale}/terms-of-use`)}>
          {t('terms of use')}
        </Link>
      </li>
      <li> | </li>
      <li className={styles.legalLink}>
        <Link href={removeDefaultLocaleFromPath(`/${locale}/privacy-policy`)}>
          {t('privacy policy')}
        </Link>
      </li>
      <li> | </li>
      <li className={styles.legalLink}>
        <a
          onClick={() => {
            // @ts-ignore - OneTrust function name
            Optanon.ToggleInfoDisplay();
          }}
        >
          {t('Do Not Sell or Share My Personal Information')}
        </a>
      </li>
    </ul>
  );
});

LegalLinks.displayName = 'LegalLinks';

const SocialLinks = ({ locale }: { locale: string }) => {
  const { t } = useTranslation();
  return (
    <ul className={styles.socialIcons}>
      <li>
        <a
          href={t('https://www.facebook.com/wonderfulpistachiosandalmonds')}
          target="_blank"
          rel="noopener noreferrer"
        >
          <span>Facebook</span>
          <FacebookIcon role="img" title="Follow us on Facebook" />
        </a>
      </li>
      {locale !== 'en-us' && (
        <li>
          <a
            href={t('https://www.instagram.com/wonderfulpistachios/')}
            target="_blank"
            rel="noopener noreferrer"
          >
            <span>Instagram</span>
            <InstagramIcon role="img" title="Follow us on Instagram" />
          </a>
        </li>
      )}
    </ul>
  );
};
